// Mantine's RichTextArea uses tiptap as it's rich text editor
// So we use tiptap's Mention extension to control the selection and render of mentions

import { ReactRenderer } from "@tiptap/react"
import tippy from "tippy.js"
import type { Instance, Props } from "tippy.js"
import type { Editor } from "@tiptap/core"

// this helper method mimics their example: // https://tiptap.dev/api/nodes/mention#usage

type RenderedComponent = ConstructorParameters<typeof ReactRenderer>[0]

export const getSuggestions = (
    members: string[],
    suggestionComponent: RenderedComponent,
    shouldDestroyComponent = false,
) => ({
    items: ({ query }) => members.filter((member) => member.toLowerCase().includes(query.toLowerCase())),
    render: () => {
        let component
        let popup: Instance<Props>[] = []

        return {
            onStart: (e) => {
                component = new ReactRenderer(suggestionComponent, {
                    props: e,
                    editor: e.editor,
                })

                if (!e.clientRect) {
                    return
                }

                popup = tippy(".ProseMirror", {
                    getReferenceClientRect: e.clientRect,
                    appendTo: () => document.body,
                    content: component.element,
                    showOnCreate: true,
                    interactive: true,
                    trigger: "manual",
                    placement: "bottom-start",
                    zIndex: 0,
                })
            },

            onUpdate(e) {
                component.updateProps(e)

                if (!e.clientRect) {
                    return
                }

                popup[0].setProps({
                    getReferenceClientRect: e.clientRect,
                })
            },

            onKeyDown(e) {
                if (e.event.key === "Escape") {
                    popup[0].hide()

                    return true
                }

                if (e.event.key === "Tab") {
                    popup[0].hide()
                }

                return component.ref?.onKeyDown(e)
            },

            onExit() {
                popup[0].destroy()
                if (shouldDestroyComponent) component?.destroy()
            },
        }
    },
})

export const insertPlaceholderFromSuggestionPopup = (
    editor: Editor,
    placeholder: string,
    formatContent: (value: string) => string,
) => {
    const text = editor.getText()
    const cursorPosition = editor.state.selection.anchor
    const placeHolderStartingPosition = findPlaceholderPosition(cursorPosition, text)
    editor.commands.setTextSelection({ from: placeHolderStartingPosition, to: cursorPosition })
    editor.commands.insertContent(formatContent(placeholder))
}

export const findPlaceholderPosition = (cursorPosition: number, text: string) => {
    for (let i = cursorPosition; i > 0; i--) {
        if (text[i] === "{") return i
    }
    return cursorPosition
}
